import { Box, Button as MuiButton, Card, Chip, Dialog, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { ReactElement, useMemo, useState } from 'react';
import {
  BooleanInput,
  Datagrid,
  DeleteButton,
  EditButton,
  FilterForm,
  FunctionField,
  List,
  NumberInput,
  Pagination,
  required,
  SelectInput,
  TextField,
  TextInput,
  TopToolbar,
  useListContext,
  useNotify,
  useRefresh,
  useTranslate,
} from 'react-admin';
import { EmptyContent } from 'src/components/empty-content';
import { SplashScreen } from 'src/components/loading-screen';
import { Iconify } from 'src/components/iconify';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { CgCreate, CgCreateProps, CgEdit, CgEditProps } from '../../../components/common';
import CustomTagInput from '../../../components/CustomTagInput';
import {
  POOL_SYNC_MODE_CHOICES,
  PROXY_PROTOCOL_CHOICES,
  PROXY_TIER_CHOICES,
  syncPool,
} from '../../../services/proxyManagerService';
import { Resources } from '../../resource';
import { resetProxyManagerCursorStore } from '../dataProvider';
import MembersDialog from './MembersDialog';

const PERMISSION_RESOURCE = 'proxy-manager';
const RESOURCE = Resources.proxyManagerPools;

const PoolForm = () => {
  const translate = useTranslate();

  return (
    <Box sx={{ pt: 2, pb: 1 }}>
      <Stack spacing={1}>
        <TextInput source="name" label="module.proxyManager.name" validate={[required()]} />
        <TextInput
          source="description"
          label="module.proxyManager.description"
          multiline
          rows={2}
        />
        <SelectInput
          source="syncMode"
          label="module.proxyManager.syncMode"
          choices={[...POOL_SYNC_MODE_CHOICES]}
          defaultValue="AUTO"
          validate={[required()]}
        />
        <Typography variant="subtitle2" sx={{ pt: 1 }}>
          {translate('module.proxyManager.filters', { _: 'Filters' })}
        </Typography>
        <SelectInput
          source="filterTier"
          label="module.proxyManager.filterTier"
          choices={[...PROXY_TIER_CHOICES]}
          resettable
        />
        <SelectInput
          source="filterProtocol"
          label="module.proxyManager.filterProtocol"
          choices={[...PROXY_PROTOCOL_CHOICES]}
          resettable
        />
        <TextInput source="filterCountryCode" label="module.proxyManager.filterCountryCode" />
        <TextInput source="filterIspName" label="module.proxyManager.filterIspName" />
        <BooleanInput source="filterIsActive" label="module.proxyManager.filterIsActive" />
        <CustomTagInput
          source="filterTags"
          label={translate('module.proxyManager.filterTags')}
          defaultValue={[]}
        />
        <Stack direction="row" spacing={2}>
          <NumberInput source="filterMinScore" label="module.proxyManager.filterMinScore" />
          <NumberInput source="filterMaxScore" label="module.proxyManager.filterMaxScore" />
        </Stack>
        <CustomTagInput
          source="healthCheckTargets"
          label={translate('module.proxyManager.healthCheckTargets')}
          defaultValue={[]}
        />
      </Stack>
    </Box>
  );
};

const POOL_EDITABLE_FIELDS = [
  'name',
  'description',
  'syncMode',
  'filterTier',
  'filterProtocol',
  'filterCountryCode',
  'filterIspName',
  'filterIsActive',
  'filterTags',
  'filterMinScore',
  'filterMaxScore',
  'healthCheckTargets',
];

const transformPool = (data: any) => {
  // Only send editable fields; never pass id or read-only fields (memberCount, createdAt...) in the body.
  const clean: Record<string, any> = {};
  POOL_EDITABLE_FIELDS.forEach((key) => {
    if (data[key] !== undefined) {
      clean[key] = data[key];
    }
  });
  ['description', 'filterTier', 'filterProtocol', 'filterCountryCode', 'filterIspName'].forEach(
    (key) => {
      if (clean[key] === '') clean[key] = null;
    }
  );
  return clean;
};

const EditPopup = (props: CgEditProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const t = useTranslate();

  const onSuccess = () => {
    notify(t('module.proxyManager.poolUpdateSuccess'), { type: 'success', autoHideDuration: 1500 });
    props.onClose?.();
    refresh();
  };
  const onError = (error: any) => {
    notify(error?.message || t('module.proxyManager.unknownError'), { type: 'error' });
  };

  return (
    <CgEdit
      {...props}
      title="module.proxyManager.editPool"
      resource={RESOURCE}
      mutationMode="pessimistic"
      transform={transformPool}
      mutationOptions={{ onSuccess, onError }}
    >
      <PoolForm />
    </CgEdit>
  );
};

const CreatePopup = (props: CgCreateProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const t = useTranslate();

  const onSuccess = () => {
    notify(t('module.proxyManager.poolCreateSuccess'), { type: 'success', autoHideDuration: 1500 });
    props.onClose?.();
    refresh();
  };
  const onError = (error: any) => {
    notify(error?.message || t('module.proxyManager.unknownError'), { type: 'error' });
  };

  return (
    <CgCreate
      {...props}
      title="module.proxyManager.createPool"
      resource={RESOURCE}
      transform={transformPool}
      mutationOptions={{ onSuccess, onError }}
    >
      <PoolForm />
    </CgCreate>
  );
};

const ListLoadingOverlay = () => {
  const { isLoading } = useListContext();
  if (!isLoading) return null;
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 10,
        bgcolor: 'rgba(255,255,255,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <SplashScreen />
    </Box>
  );
};

const PoolListActions = ({ onCreate }: { onCreate: () => void }) => {
  const { filterValues, setFilters, setPage } = useListContext();
  const translate = useTranslate();
  const hasActiveFilters = Object.keys(filterValues ?? {}).length > 0;

  const ACTIVE_CHOICES = useMemo(
    () => [
      { id: 'true', name: translate('common.active', { _: 'Active' }) },
      { id: 'false', name: translate('common.inactive', { _: 'Inactive' }) },
    ],
    [translate]
  );

  const handleResetFilters = () => {
    resetProxyManagerCursorStore(RESOURCE);
    setPage(1);
    setFilters({}, []);
  };

  return (
    <TopToolbar sx={{ alignItems: 'center', p: 2, width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          gap: 2,
          flexWrap: 'wrap',
          '& form': {
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            flex: 1,
            minHeight: '40px',
          },
          '& .ra-input': { mt: 0, mb: 0 },
          '& .MuiFormControl-root': { mt: 0, mb: 0 },
          '& .MuiInputBase-root': { minHeight: 40 },
        }}
      >
        <FilterForm
          filters={[
            <SelectInput
              key="syncMode"
              source="syncMode"
              label="module.proxyManager.syncMode"
              choices={[...POOL_SYNC_MODE_CHOICES]}
              alwaysOn
              resettable
              emptyText={translate('actions.all')}
              size="small"
              sx={{ minWidth: 140 }}
            />,
            <SelectInput
              key="isActive"
              source="isActive"
              label="module.proxyManager.status"
              choices={ACTIVE_CHOICES}
              alwaysOn
              resettable
              emptyText={translate('actions.all')}
              size="small"
              sx={{ minWidth: 120 }}
            />,
          ]}
        />
        <MuiButton
          variant="outlined"
          size="medium"
          onClick={handleResetFilters}
          disabled={!hasActiveFilters}
        >
          {translate('common.reset')}
        </MuiButton>
        <PermissionGuard resource={PERMISSION_RESOURCE} action="create" fallback={<></>}>
          <MuiButton variant="contained" size="medium" onClick={onCreate}>
            {translate('module.proxyManager.createPool')}
          </MuiButton>
        </PermissionGuard>
      </Box>
    </TopToolbar>
  );
};

const PoolPagination = () => {
  const { setPage } = useListContext();
  const refresh = useRefresh();
  const translate = useTranslate();

  const handleRefresh = () => {
    resetProxyManagerCursorStore(RESOURCE);
    setPage(1);
    refresh();
  };

  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} px={2} py={1}>
      <MuiButton variant="outlined" size="small" onClick={handleRefresh}>
        {translate('common.refresh', { _: 'Refresh' })}
      </MuiButton>
      <Pagination rowsPerPageOptions={[20, 50, 100, 200]} />
    </Box>
  );
};

export const PoolsPanel = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [popup, setPopup] = useState<ReactElement>();
  const [membersPool, setMembersPool] = useState<{
    id: string;
    name: string;
    healthCheckTargets: string[];
  } | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);

  const handleSync = async (record: any) => {
    setSyncingId(record.id);
    try {
      await syncPool(record.id);
      notify(translate('module.proxyManager.syncSuccess', { _: 'Pool synced successfully' }), {
        type: 'success',
        autoHideDuration: 1500,
      });
      refresh();
    } catch (error: any) {
      notify(error?.message || translate('module.proxyManager.unknownError'), { type: 'error' });
    } finally {
      setSyncingId(null);
    }
  };

  return (
    <>
        <Card sx={{ mt: 2, pb: 2 }}>
          <List
            resource={RESOURCE}
            actions={
              <PoolListActions
                onCreate={() => setPopup(<CreatePopup onClose={() => setPopup(undefined)} />)}
              />
            }
            empty={false}
            perPage={20}
            exporter={false}
            pagination={<PoolPagination />}
            sort={{ field: 'createdAt', order: 'DESC' }}
          >
            <Box sx={{ overflow: 'auto', position: 'relative' }}>
              <ListLoadingOverlay />
              <Datagrid
                rowClick={false}
                empty={<EmptyContent />}
                bulkActionButtons={false}
                sx={{ '& .RaDatagrid-tableWrapper': { overflowX: 'auto' } }}
              >
                <TextField source="name" label="module.proxyManager.name" sortable={false} />
                <FunctionField
                  label="module.proxyManager.description"
                  sortable={false}
                  render={(record: any) => record?.description || '-'}
                />
                <FunctionField
                  label="module.proxyManager.syncMode"
                  sortable={false}
                  render={(record: any) => (
                    <Chip
                      label={record?.syncMode}
                      color={record?.syncMode === 'AUTO' ? 'info' : 'default'}
                      size="small"
                    />
                  )}
                />
                <FunctionField
                  label="module.proxyManager.filters"
                  sortable={false}
                  render={(record: any) => {
                    const parts = [
                      record?.filterTier,
                      record?.filterProtocol,
                      record?.filterCountryCode,
                      record?.filterIspName,
                      ...(record?.filterTags ?? []),
                    ].filter(Boolean);
                    return (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {parts.length
                          ? parts.map((p: string, i: number) => (
                              <Chip key={`${p}-${i}`} label={p} size="small" variant="outlined" />
                            ))
                          : '-'}
                      </Stack>
                    );
                  }}
                />
                <FunctionField
                  label="module.proxyManager.memberCount"
                  sortable={false}
                  render={(record: any) => record?.memberCount ?? 0}
                />
                <FunctionField
                  label="module.proxyManager.status"
                  sortable={false}
                  render={(record: any) => (
                    <Chip
                      label={
                        record?.isActive
                          ? translate('common.active', { _: 'Active' })
                          : translate('common.inactive', { _: 'Inactive' })
                      }
                      color={record?.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  )}
                />
                <FunctionField
                  label="module.proxyManager.updatedAt"
                  sortable={false}
                  render={(record: any) => (
                    <Typography variant="body2" noWrap>
                      {record?.updatedAt ? dayjs(record.updatedAt).format('YYYY-MM-DD HH:mm') : '-'}
                    </Typography>
                  )}
                />
                <FunctionField
                  label="common.actions"
                  textAlign="right"
                  render={(record: any) => (
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <MuiButton
                        size="small"
                        variant="text"
                        onClick={() =>
                          setMembersPool({
                            id: record.id,
                            name: record.name,
                            healthCheckTargets: record.healthCheckTargets ?? [],
                          })
                        }
                      >
                        {translate('module.proxyManager.members', { _: 'Members' })}
                      </MuiButton>
                      <PermissionGuard
                        resource={PERMISSION_RESOURCE}
                        action="update"
                        fallback={<></>}
                      >
                        <MuiButton
                          size="small"
                          variant="text"
                          color="info"
                          disabled={syncingId === record.id}
                          startIcon={<Iconify icon="mdi:cloud-sync" />}
                          onClick={() => handleSync(record)}
                        >
                          {translate('module.proxyManager.sync', { _: 'Sync' })}
                        </MuiButton>
                      </PermissionGuard>
                      <PermissionGuard
                        resource={PERMISSION_RESOURCE}
                        action="update"
                        fallback={<></>}
                      >
                        <EditButton
                          onClick={(e) => {
                            e.preventDefault();
                            setPopup(
                              <EditPopup id={record.id} onClose={() => setPopup(undefined)} />
                            );
                          }}
                        />
                      </PermissionGuard>
                      <PermissionGuard
                        resource={PERMISSION_RESOURCE}
                        action="delete"
                        fallback={<></>}
                      >
                        <DeleteButton
                          resource={RESOURCE}
                          record={record}
                          mutationMode="pessimistic"
                          redirect={false}
                        />
                      </PermissionGuard>
                    </Stack>
                  )}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>

      <Dialog open={Boolean(popup)} onClose={() => setPopup(undefined)} maxWidth="sm" fullWidth>
        {popup}
      </Dialog>

      <MembersDialog
        open={Boolean(membersPool)}
        poolId={membersPool?.id}
        poolName={membersPool?.name}
        healthCheckTargets={membersPool?.healthCheckTargets}
        onClose={() => setMembersPool(null)}
      />
    </>
  );
};

export default PoolsPanel;
